import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, DatePicker, Modal, Select, Space, Table, TableColumnsType, message } from "antd";
import { CloudUploadOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";

import StringUtils from "../../../../../../util/StringUtils";
import FileUtils from "../../../../../../util/FileUtils";
import ByteUtils from "../../../../../../util/ByteUtils";
import SessionService from "../../../../../../services/session/SessionService";
import Api from "../../../../../../api/Api";
import WaveformFileApi from "../../../../../../api/WaveformFileApi";
import { FilterBar, FilterItem, FilterItemLabel, OfflineMetricsDataFileManagementWrapper, TableWrapper } from "./OfflineMetricsDataFile.styled";
import OfflineMetricsDataFileApi from "../../../../../../api/OfflineMetricsDataFIleApi";

interface WaveformFileProps {
    deviceView: any
}

let OfflineMetricsDataFile: React.FC<WaveformFileProps> = (props)=> {
    // params
    let deviceView = props.deviceView;

    // api
    let offlineMetricsDataFileApi = new OfflineMetricsDataFileApi();
    let navigate = useNavigate();
    let [messageApi, contextHolder] = message.useMessage();

    // state
    let [loading, setLoading] = useState(true);
    let [offlineMetricsDataFileViews, setOfflineMetricsDataFileViews] = useState([]);
    let [startTime, setStartTime] = useState<dayjs.Dayjs>();
    let [endTime, setEndTime] = useState<dayjs.Dayjs>();

    // functions
    let loadOfflineMetricsDataFileViews = async ()=> {
        setLoading(true);
        let offlineMetricsDataFileViews = (await offlineMetricsDataFileApi.queryViews({
            deviceId: deviceView.device.id,
            startTime: startTime?.toDate().getTime(),
            endTime: endTime?.toDate().getTime(),
        })).data.data;
        setOfflineMetricsDataFileViews(offlineMetricsDataFileViews);
        setLoading(false);
    };

    let formatFileName = (fileName: string)=> {
        if (fileName.lastIndexOf('/') >= 0) {
            return fileName.substring(fileName.lastIndexOf('/') + 1);
        }
        return fileName;
    }

    let formatTimestampUs = (timestampUs)=> {
        if (timestampUs == null) {
            return '';
        }
        return moment(parseInt(timestampUs)/1000).format('YYYY-MM-DD HH:mm:ss');
    };

    let getDownloadDataFileUrl = (dataFileView)=> {
        let downloadUrl = Api.URL_PREFIX + `/offline-metrics-data-file/download?id=${dataFileView.dataFile.id}&authorization=${SessionService.getInstance().getAuthoriazation()}`;
        return downloadUrl;
    };

    let onDateTimeRangeChange = (dateTimeRangeValue: any[])=> {
        setStartTime(dateTimeRangeValue[0]);
        setEndTime(dateTimeRangeValue[1]);
    };


    let onUploadDataFile = ()=> {
        let fileSelect=document.createElement('input');
        fileSelect.type='file';
        fileSelect.multiple=true;
        fileSelect.style.display = 'none';
        fileSelect.addEventListener('click', (event)=>{
            event.stopPropagation();
        });

        let onSelectFile = async () => {
            for (let i = 0; i < fileSelect.files.length; i++) {
                try {
                    let file = fileSelect.files[i];
                    let response = await offlineMetricsDataFileApi.upload(file);
                    messageApi.success('文件导入成功');
                } catch (e) {
                    messageApi.error('文件导入失败:' + e);
                }
            }
        };
        fileSelect.addEventListener('change', (event)=>{
            event.stopPropagation();
            onSelectFile();
        });

        fileSelect.click();
    };

    // effect
    useEffect(()=> {
        loadOfflineMetricsDataFileViews();
    }, [startTime, endTime]);

    // render
    const dataFileColumns: TableColumnsType<any> = [
        {
            title: '日期/文件名',
            dataIndex: 'name',
            key: 'name',
            width: '200px',
            render: (text, record, index)=> {
                return <div style={{whiteSpace: 'nowrap', textAlign: 'left'}}>{formatFileName(record.waveformFile.name)}</div>
            },
        },
        {
            title: '起始时间',
            dataIndex: 'startTime',
            key: 'startTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{formatTimestampUs(record.waveformFile.startTimestampUs)}</span>
            }
        },
        {
            title: '结束时间',
            dataIndex: 'endTime',
            key: 'endTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{formatTimestampUs(record.waveformFile.endTimestampUs)}</span>
            }
        },
        {
            title: '文件大小',
            dataIndex: 'size',
            key: 'size',
            align: 'center',
            render: (text, record, index)=> {
                let size = Number(record.waveformFile.size);
                if (size == null) {
                    return StringUtils.emptyToSlash('');
                }
                return ByteUtils.formatBytes(size);
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return record.waveformFile.id && <Space size="middle">
                    {record.waveformFile.type && <a href={getDownloadDataFileUrl(record)}>下载</a>}
                </Space>
            }
        }
    ];

    return <OfflineMetricsDataFileManagementWrapper>
            {contextHolder}
            <FilterBar>
                <Space size="middle">
                    <FilterItem>
                        <FilterItemLabel>时间范围</FilterItemLabel>
                        <DatePicker.RangePicker
                            style={{width: '330px'}}
                            showTime={{ format: 'HH:mm:ss' }}
                            format="YYYY-MM-DD HH:mm:ss"
                            defaultValue={[dayjs().add(-1, 'd'), dayjs()]}
                            value={[startTime, endTime]}
                            allowEmpty={[false, true]}
                            onOk={onDateTimeRangeChange}
                        />
                    </FilterItem>
                </Space>


                <Space size="middle">
                    <Button type="primary" onClick={onUploadDataFile} icon={<CloudUploadOutlined />}>上传数据文件</Button>
                </Space>
            </FilterBar>
            <TableWrapper>
                <Table 
                    style={{height: '100%'}}
                    rowKey={(record)=> record.waveformFile.name}
                    columns={dataFileColumns} 
                    dataSource={offlineMetricsDataFileViews}
                    loading={loading}
                    pagination={false}
                    scroll={{y: 'calc(100vh - 350px)'}}
                />
            </TableWrapper>
    </OfflineMetricsDataFileManagementWrapper>
};

export default OfflineMetricsDataFile;
